"use client";

import { ArrowDown2 } from "iconsax-reactjs";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useVoteBlogComment } from "@/hooks/blog/useVoteBlogComment";
import { useDateFormatter } from "@/lib/i18n/date";
import { cn } from "@/lib/utils";
import { CommentForm } from "./CommentForm";
import { CommentReplies } from "./CommentReplies";
import { VoteButtons } from "./VoteButtons";

export interface CommentNode {
  id: string;
  author_name: string;
  created_at: string;
  content: string;
  like_count: number;
  dislike_count: number;
  my_vote: "like" | "dislike" | null;
  reply_count?: number;
}

interface CommentItemProps {
  comment: CommentNode;
  slug: string;
  listKey: unknown[];
  isReply?: boolean;
  onRequireLogin: () => void;
  isLoggedIn: boolean;
}

const initial = (name: string) => name.trim().charAt(0) || "؟";

export const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  slug,
  listKey,
  isReply = false,
  onRequireLogin,
  isLoggedIn,
}) => {
  const t = useTranslations("blog");
  const df = useDateFormatter();
  const [showReplies, setShowReplies] = useState(false);
  const [replying, setReplying] = useState(false);
  const vote = useVoteBlogComment();

  const onVote = (value: "like" | "dislike") => {
    if (!isLoggedIn) return onRequireLogin();
    vote.mutate({ commentId: comment.id, value, listKey });
  };

  return (
    <div className="flex gap-3">
      <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-bg-secondary text-lg font-bold text-text-primary">
        {initial(comment.author_name)}
      </div>
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <span className="font-bold text-text-primary">
            {comment.author_name}
          </span>
          <span className="text-xs text-text-disabled">
            {df.relative(comment.created_at)}
          </span>
        </div>
        <p className="text-sm leading-7 text-text-primary">{comment.content}</p>
        <div className="flex items-center gap-4">
          <VoteButtons
            likeCount={comment.like_count}
            dislikeCount={comment.dislike_count}
            myVote={comment.my_vote}
            onVote={onVote}
            disabled={vote.isPending}
          />
          {!isReply && (
            <button
              type="button"
              onClick={() =>
                isLoggedIn ? setReplying((v) => !v) : onRequireLogin()
              }
              className="text-sm text-text-secondary"
            >
              {t("comment.reply")}
            </button>
          )}
        </div>

        {replying && (
          <CommentForm
            slug={slug}
            parentId={comment.id}
            onDone={() => setReplying(false)}
            isLoggedIn={isLoggedIn}
            onRequireLogin={onRequireLogin}
            compact
          />
        )}

        {!isReply && (comment.reply_count ?? 0) > 0 && (
          <>
            <button
              type="button"
              onClick={() => setShowReplies((v) => !v)}
              className="inline-flex items-center gap-1 text-sm text-accent"
            >
              {t("comment.replies")}
              <ArrowDown2
                size={16}
                className={cn(
                  "transition-transform",
                  showReplies && "rotate-180",
                )}
              />
            </button>
            {showReplies && (
              <CommentReplies
                commentId={comment.id}
                slug={slug}
                isLoggedIn={isLoggedIn}
                onRequireLogin={onRequireLogin}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
